import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import {
  fetchUserExperimentsSuccess,
  selectActiveExperiments,
  selectAcknowledgedExperiments,
  selectIsUserExperimentLoaded,
  updateUserExperiment,
} from '../../../store/userExperiment'
import { ExperimentId } from '../../hooks/useExperiment'
import { useUserExperimentRepository } from '../../../services/adaptors/userExperimentRepositoryAdaptor'
import store, { AllState } from '../../../store'
import { showAlert } from '../../../store/globalAlert'
import { AnnouncementBanner } from '../../components/announcement-banner'

/** バナー用のメタ情報（i18n キー） */
type BannerMeta = {
  titleKey: string
  descriptionKey: string
}

/** バナーを表示する実験とそのメタ情報 */
const EXPERIMENT_BANNERS: Record<ExperimentId, BannerMeta> = {
  'wbs-status-column-layout': {
    titleKey: 'experimentBanner.title',
    descriptionKey: 'experimentBanner.wbsStatusColumnLayout.description',
  },
}

/** バナー対象の実験ID一覧 */
const BANNER_EXPERIMENT_IDS = Object.keys(EXPERIMENT_BANNERS) as ExperimentId[]

export const ExperimentBanner: React.FC = () => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const repository = useUserExperimentRepository()
  const isLoaded = useSelector((state: AllState) =>
    selectIsUserExperimentLoaded(state)
  )
  const activeExperiments = useSelector(selectActiveExperiments)
  const acknowledgedExperiments = useSelector(selectAcknowledgedExperiments)

  // 初期化: 設定をロード
  useEffect(() => {
    if (isLoaded) return

    const loadSettings = async () => {
      const settings = await repository.fetchAll()
      dispatch(fetchUserExperimentsSuccess(settings))
    }
    loadSettings()
  }, [dispatch, isLoaded, repository])

  // バナー対象 AND 現在の画面で表示対象 AND 未acknowledge の実験を探す
  const experimentToShow = useMemo(() => {
    if (!isLoaded) return undefined

    for (const expId of BANNER_EXPERIMENT_IDS) {
      if (
        activeExperiments.includes(expId) &&
        !acknowledgedExperiments.includes(expId)
      ) {
        return expId
      }
    }
    return undefined
  }, [isLoaded, activeExperiments, acknowledgedExperiments])

  const banner = experimentToShow
    ? EXPERIMENT_BANNERS[experimentToShow]
    : undefined

  // 新しい表示を継続（useNew = true で保存）
  const onKeepNew = useCallback(async () => {
    if (!experimentToShow) return
    await repository.update(experimentToShow, true)
    dispatch(updateUserExperiment(experimentToShow, true))
  }, [experimentToShow, repository, dispatch])

  // 従来の表示に戻す（確認後リロード）
  const onRevert = useCallback(() => {
    if (!experimentToShow) return
    store.dispatch(
      showAlert({
        message: intl.formatMessage({ id: 'experimentBanner.revertConfirm' }),
        action: async () => {
          await repository.update(experimentToShow, false)
          dispatch(updateUserExperiment(experimentToShow, false))
          window.location.reload()
        },
        cancel: () => {
          // キャンセル時は何もしない
        },
      })
    )
  }, [experimentToShow, repository, dispatch, intl])

  // デモ環境では実験バナーを表示しない
  if (process.env.FLAGXS_DEMO_MODE === 'true') return null

  if (!experimentToShow || !banner) return null

  return (
    <AnnouncementBanner
      title={intl.formatMessage({ id: banner.titleKey })}
      description={intl.formatMessage({ id: banner.descriptionKey })}
      linkText={intl.formatMessage({ id: 'experimentBanner.revertLink' })}
      onLinkClick={onRevert}
      onClose={onKeepNew}
    />
  )
}
